import React from "react";
import { Box, Paper, Grid, Avatar, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import moment from "moment";

const MEDALS = [
  { Icon: EmojiEventsIcon, color: "#b7791f", bg: "#fef3c7", ring: "#f59e0b" },
  { Icon: MilitaryTechIcon, color: "#64748b", bg: "#f1f5f9", ring: "#94a3b8" },
  { Icon: WorkspacePremiumIcon, color: "#9a5b32", bg: "#fdf1e8", ring: "#c2703d" },
];

const formatTime = (minutes) => moment().startOf("day").add(minutes || 0, "minutes").format("HH[h] mm[m]");

// Ranquear "menor tempo de espera" ou "melhor nota" só pela quantidade
// de atendimentos, sem piso, deixa quem atendeu 1-2 tickets por sorte
// aparecer na frente de quem atende muito mais - por isso essas duas
// listas exigem um volume minimo de atendimentos pra entrar no ranking.
const buildRankings = (minTicketsForQuality) => [
  {
    key: "tickets",
    title: "Mais atendimentos",
    icon: "🏆",
    sort: (a, b) => (b.tickets || 0) - (a.tickets || 0),
    detail: (a) => `${a.tickets} atendimentos`,
  },
  {
    key: "avgWaitTime",
    title: "Menor tempo de espera",
    icon: "⚡",
    sort: (a, b) => (a.avgWaitTime ?? Infinity) - (b.avgWaitTime ?? Infinity),
    detail: (a) => `Espera ${formatTime(a.avgWaitTime)} · ${a.tickets} atend.`,
    filter: (a) => a.avgWaitTime != null && (a.tickets || 0) >= minTicketsForQuality,
  },
  {
    key: "rating",
    title: "Melhor nota",
    icon: "⭐",
    sort: (a, b) => (b.rating || 0) - (a.rating || 0),
    detail: (a) => `Nota ${a.rating ?? "-"} · ${a.tickets} atend.`,
    filter: (a) => (a.countRating || 0) > 0 && (a.tickets || 0) >= minTicketsForQuality,
  },
];

function MiniPodium({ title, icon, ranked, detail }) {
  if (ranked.length === 0) return null;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <span role="img" aria-label={title} style={{ fontSize: 16 }}>{icon}</span>
        <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {ranked.map((a, i) => {
          const m = MEDALS[i];
          return (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                p: 1,
                borderRadius: 2,
                border: `1.5px solid ${m.ring}`,
                bgcolor: m.bg,
              }}
            >
              <Avatar sx={{ bgcolor: "#fff", border: `2px solid ${m.ring}`, color: m.color, width: 34, height: 34 }}>
                <m.Icon fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight="bold" noWrap sx={{ color: "#1e293b" }}>
                  {a.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "#475569" }}>
                  {detail(a)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function RankingPodium({ attendants }) {
  const base = (attendants || []).filter((a) => (a.tickets || 0) > 0);

  if (base.length === 0) {
    return null;
  }

  const maxTickets = Math.max(...base.map((a) => a.tickets || 0));
  const minTicketsForQuality = Math.max(3, Math.ceil(maxTickets * 0.2));
  const RANKINGS = buildRankings(minTicketsForQuality);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Ranking de Performance
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Tempo de espera e nota consideram só quem atendeu pelo menos {minTicketsForQuality} tickets no período.
      </Typography>
      <Grid container spacing={3}>
        {RANKINGS.map((r) => {
          const pool = r.filter ? base.filter(r.filter) : base;
          const ranked = [...pool].sort(r.sort).slice(0, 3);
          return (
            <Grid item xs={12} md={4} key={r.key}>
              <MiniPodium title={r.title} icon={r.icon} ranked={ranked} detail={r.detail} />
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
}
